import type { FormEvent } from 'react';
import React, { useMemo, useState } from 'react';
import { AudioElement, GroupElement, ImageElement, VideoElement } from '@bynder-studio/render-core';
import { Divider, Form } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import useForceUpdate from '~/hooks/useForceUpdate';
import useEditor from '~/hooks/useEditor';
import useEditorSelectedElement from '~/hooks/useEditorSelectedElement';
import { useEffectivelyLocked } from 'packages/hooks/useEffectivelyLocked';
import useEditorSidebarShortcuts from 'packages/hooks/shortcuts/useEditorSidebarShortcuts';
import Rotation from 'packages/pages/editor/RightSideMenu/Shots/Rotation';
import SettingsTabs from '../Tabs';
import ElementAlignment from '../ElementAlignment';
import AllowToggleVisibility from '../AllowToggleVisibility';
import PersonalUploads from '../PersonalUploads';
import Width from '../Size/Width';
import LockToggler from '../Size/LockToggle';
import Height from '../Size/Height';
import HorizontalPosition from '../Position/Horizontal';
import VerticalPosition from '../Position/Vertical';
import { BlendMode } from '../BlendMode';
import Animations from '../../Animations';
import Masking from '../Masking';
import { flattenTree } from '~/common/editor/helpers/elementtree';
import { SizeFormRow } from '../index.styled';
import { FormSectionContent, InspectorContent, InspectorHeader, Section } from '../../index.styled';
import features from 'packages/configs/features';
import useAccessRights from 'packages/hooks/useAccessRights';
import ContentProperty from '../ContentProperty';
import AssetSourceMultiple from '../AssetSource/AssetSourceMultiple';
import DynamicElementSelection from './DynamicElementSelection';

const Group = () => {
    const forceUpdate = useForceUpdate();
    const { translate } = useTranslate();
    const { isPlatformAllowed } = useAccessRights();
    const { creativeModel, creativeType, manipulationRenderer } = useEditor();
    const { selectedElement, selectedElements } = useEditorSelectedElement<GroupElement>();
    const isLockedByParent = useEffectivelyLocked();

    const groupElements = useMemo(
        () =>
            Object.values(flattenTree(selectedElements)).filter((value) => !(value instanceof GroupElement)) as (
                | ImageElement
                | VideoElement
                | AudioElement
            )[],
        [selectedElements],
    );

    const groupControllableElements = useMemo(
        () =>
            groupElements.filter(
                (element) =>
                    element instanceof ImageElement ||
                    element instanceof VideoElement ||
                    element instanceof AudioElement,
            ) as (ImageElement | VideoElement | AudioElement)[],
        [selectedElements],
    );

    const hasControllableElement = useMemo(
        () =>
            groupElements.some(
                (element) =>
                    element instanceof ImageElement ||
                    element instanceof VideoElement ||
                    element instanceof AudioElement,
            ),
        [selectedElements],
    );

    const tabs = [
        { value: 'group', label: translate('editor.sidebar.shots.group.tabs.text') },
        { value: 'group_animation', label: translate('editor.sidebar.shots.animation.tabs.text') },
    ];

    const [activeTab, setTab] = useState(tabs[0].value);

    const defaultProps = {
        selectedElement,
        creativeModel,
        disabled: isLockedByParent,
    };

    useEditorSidebarShortcuts({
        setAnimationsTab: () => {
            if (creativeType === 'VIDEO') {
                setTab(tabs[1].value);
            }
        },
    });

    return (
        <>
            {creativeType === 'VIDEO' && (
                <InspectorHeader aria-labelledby="shots-group-tabs-settings">
                    <SettingsTabs tabs={tabs} activeTab={activeTab} onChange={(value) => setTab(value)} />
                </InspectorHeader>
            )}
            <InspectorContent withTabs={creativeType === 'VIDEO'}>
                {activeTab === tabs[0].value && (
                    <>
                        <Section aria-labelledby="shots-alignment-settings">
                            <ElementAlignment creativeModel={creativeModel} selectedElements={selectedElements} />
                        </Section>
                        <Divider />
                        <Section aria-labelledby="shots-size-settings">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <SizeFormRow>
                                    <Width {...defaultProps} creativeType={creativeType} />
                                    <LockToggler selectedElement={selectedElement} forceUpdate={forceUpdate} />
                                    <Height {...defaultProps} creativeType={creativeType} />
                                </SizeFormRow>
                                <Form.Row>
                                    <HorizontalPosition {...defaultProps} />
                                    <VerticalPosition {...defaultProps} />
                                </Form.Row>
                            </FormSectionContent>
                            {isPlatformAllowed([features.GROUP_ROTATION]) && (
                                <Form.Row>
                                    <Rotation {...defaultProps} />
                                </Form.Row>
                            )}
                        </Section>
                        <Divider />
                        {isPlatformAllowed([features.DYNAMIC_ELEMENT_SELECTION]) && (
                            <DynamicElementSelection
                                selectedElement={selectedElement}
                                creativeModel={creativeModel}
                                disabled={isLockedByParent}
                            />
                        )}
                        <Section aria-labelledby="shots-content-property-settings">
                            <Form onSubmit={preventSubmit}>
                                <Form.Group>
                                    <Form.Label>{translate('editor.element.content_property')}</Form.Label>
                                    <ContentProperty creativeModel={creativeModel} selectedElements={groupElements} />
                                </Form.Group>
                            </Form>
                        </Section>
                        <Divider />
                        {hasControllableElement && (
                            <>
                                <Section aria-labelledby="shots-asset-source">
                                    <AssetSourceMultiple
                                        creativeModel={creativeModel}
                                        selectedElements={groupControllableElements}
                                    />
                                </Section>
                                <Divider />
                                <Section aria-labelledby="shots-image-asset-source">
                                    <Form onSubmit={preventSubmit}>
                                        <Form.Group>
                                            <Form.Label>{translate('editor.element.upload_control')}</Form.Label>
                                            <PersonalUploads selectedElements={groupControllableElements} />
                                        </Form.Group>
                                    </Form>
                                </Section>
                                <Divider />
                            </>
                        )}
                        <Section aria-labelledby="shots-image-visibility">
                            <Form onSubmit={preventSubmit}>
                                <Form.Group>
                                    <Form.Label>{translate('editor.element.visibility_control')}</Form.Label>
                                    <AllowToggleVisibility
                                        creativeModel={creativeModel}
                                        selectedElements={groupElements}
                                    />
                                </Form.Group>
                            </Form>
                        </Section>
                        <Divider />
                        <Section aria-labelledby="shots-video-masking-settings">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <Form.Group>
                                    <Form.Label>{translate('editor.element.mask')}</Form.Label>
                                    <Masking
                                        {...defaultProps}
                                        creativeType={creativeType}
                                        manipulationRenderer={manipulationRenderer}
                                    />
                                </Form.Group>
                            </FormSectionContent>
                        </Section>
                        <Divider />
                        <Section aria-labelledby="shots-video-blend-settings">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <Form.Group>
                                    <BlendMode disabled={defaultProps.disabled} />
                                </Form.Group>
                            </FormSectionContent>
                        </Section>
                    </>
                )}
                {activeTab === tabs[1].value && <Animations element={selectedElement} disabled={isLockedByParent} />}
            </InspectorContent>
        </>
    );
};

export default Group;

function preventSubmit(e: FormEvent) {
    e.preventDefault();
}
